import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Dropzone from 'react-dropzone';
import ImageCropModal from './ImageCropModal';
import { fileToDataUrl } from './cropUtils';
import { toastError } from './toastHelper';

const ImageUpload = ({
  imageUrl,
  onUpload,
  onDelete,
  uploading = false,
  uploadProgress = null,
  selectedFile = null,
  placeholder = {
    title: 'Click to upload',
    subtitle: 'or drag and drop',
    formats: 'SVG, PNG, JPG or GIF'
  },
  accept = "image/*",
  disabled = false,
  aspectRatio = 2 / 1
}) => {
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [fileNameToCrop, setFileNameToCrop] = useState('');
  const getFileExtension = (filename) => {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return 'FILE';
    const extension = filename.substring(lastDot + 1).toUpperCase();
    return extension.length > 4 ? extension.substring(0, 4) : extension;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const UploadProgressCard = ({ upload }) => (
    <div style={{
      border: '1px solid var(--od-c-e9eaeb, #E9EAEB)',
      borderRadius: '8px',
      padding: '12px 16px',
      marginBottom: '12px',
      backgroundColor: 'var(--od-c-ffffff, #ffffff)'
    }}>
      <div className="d-flex align-items-start">
        <div style={{
          width: '32px',
          height: '32px',
          backgroundColor: 'var(--od-c-2563eb, #2563EB)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          flexShrink: 0
        }}>
          <span style={{
            color: 'white',
            fontSize: '10px',
            fontWeight: '600'
          }}>
            {getFileExtension(upload.name)}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--od-c-374151, #374151)',
            marginBottom: '0px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {upload.name}
          </div>
          <div style={{
            fontSize: '14px',
            color: 'var(--od-c-6b7280, #6B7280)',
            marginBottom: '2px'
          }}>
            {upload.size}
          </div>
          {upload.progress < 100 && (
            <div className="d-flex align-items-center">
              <div style={{
                flex: 1,
                height: '4px',
                backgroundColor: 'var(--od-c-e5e7eb, #E5E7EB)',
                borderRadius: '2px',
                overflow: 'hidden',
                marginRight: '12px'
              }}>
                <div style={{
                  width: `${upload.progress}%`,
                  height: '100%',
                  backgroundColor: 'var(--od-c-2563eb, #2563EB)',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--od-c-374151, #374151)',
                flexShrink: 0
              }}>
                {upload.progress}%
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const UploadedFileCard = ({ file, imageUrl, onDelete, onEdit, disabled }) => (
    <div style={{
      border: '1px solid var(--od-c-e9eaeb, #E9EAEB)',
      borderRadius: '12px',
      padding: '16px',
      backgroundColor: 'var(--od-c-ffffff, #ffffff)'
    }}>
      <div className="d-flex align-items-start" style={{"gap": "4px"}}>
        <div className="d-flex" style={{"flex": "1", "minWidth": "0", "gap": "12px"}}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '1px solid var(--od-c-e9eaeb, #E9EAEB)',
            borderRadius: '8px',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: 'var(--od-c-ffffff, #ffffff)'
          }}>
            <img
              src={imageUrl}
              alt={file.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
          <div style={{"flex": "1", "minWidth": "0"}}>
            <div style={{
              fontSize: '14px',
              fontWeight: '400',
              color: 'var(--od-c-414651, #414651)',
              lineHeight: '20px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {file.name}
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: '400',
              color: 'var(--od-c-535862, #535862)',
              lineHeight: '20px'
            }}>
              {file.size}
            </div>
          </div>
        </div>
        <div className="d-flex" style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
          <button
            type="button"
            onClick={() => !disabled && onEdit()}
            disabled={disabled}
            style={{
              background: 'none',
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              padding: '8px',
              color: 'var(--od-c-a4a7ae, #A4A7AE)',
              fontSize: '16px'
            }}
            title="Edit image"
          >
            <i className="far fa-pencil"></i>
          </button>
          <button
            type="button"
            onClick={() => !disabled && onDelete()}
            disabled={disabled}
            style={{
              background: 'none',
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              padding: '8px',
              color: 'var(--od-c-a4a7ae, #A4A7AE)',
              fontSize: '16px'
            }}
            title="Remove image"
          >
            <i className="far fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>
  );

  const handleDrop = async (files) => {
    if (files.length > 0 && !disabled && !uploading) {
      const file = files[0];
      try {
        const dataUrl = await fileToDataUrl(file);
        setImageToCrop(dataUrl);
        setFileNameToCrop(file.name);
        setCropModalOpen(true);
      } catch {
        toastError('Failed to read image file. Please try again.');
      }
    }
  };

  const handleCropComplete = (croppedFile) => {
    onUpload(croppedFile);
    setImageToCrop(null);
    setFileNameToCrop('');
  };

  const handleCropModalClose = () => {
    setCropModalOpen(false);
    setImageToCrop(null);
    setFileNameToCrop('');
  };

  const handleEditImage = () => {
    if (!imageUrl || disabled || uploading) return;

    setImageToCrop(imageUrl);
    setFileNameToCrop(selectedFile?.name || 'cropped-image.jpg');
    setCropModalOpen(true);
  };

  return (
    <div>
      {imageUrl ? (
        <UploadedFileCard
          file={selectedFile || { name: 'Image', size: '' }}
          imageUrl={imageUrl}
          onDelete={onDelete}
          onEdit={handleEditImage}
          disabled={disabled || uploading}
        />
      ) : (
        <Dropzone 
          maxFiles={1}
          accept={accept}
          onDrop={handleDrop}
          disabled={disabled || uploading}
        >
          {({getRootProps, getInputProps}) => (
            <section 
              {...getRootProps()}
              style={{
                border: '1px solid var(--od-c-e9eaeb, #E9EAEB)',
                borderRadius: '12px',
                padding: '16px 24px',
                textAlign: 'center',
                cursor: disabled || uploading ? 'not-allowed' : 'pointer',
                backgroundColor: uploading ? 'var(--od-c-f8f9fa, #f8f9fa)' : 'transparent',
                opacity: disabled ? 0.5 : 1
              }}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <div>
                  <img src="/uploading-loading.gif" 
                       style={{width: "25px", marginBottom: '10px'}} 
                       alt="Uploading" />
                  <p className="mb-0">Uploading...</p>
                </div>
              ) : (
                <div>
                  <div className='d-flex align-items-center justify-content-center mb-3 shadow-1'
                        style={{
                          borderRadius: '8px',
                          border: '1px solid var(--od-c-e9eaeb, #E9EAEB)',
                          width: "40px",
                          height: "40px",
                          margin: "0 auto",
                        }}>
                    <i className="far fa-cloud-upload-alt text-dark" 
                       style={{fontSize: "16px"}}></i>
                  </div>
                  <p className="mb-0"
                      style={{
                        fontSize: '14px',
                        color: "var(--od-c-535862, #535862)"
                      }}>
                    <strong style={{color: 'var(--od-accent, #136395)'}}>{placeholder.title}</strong> {placeholder.subtitle}<br/>
                    <span style={{fontSize: '12px'}}>{placeholder.formats}</span>
                  </p>
                </div>
              )}
            </section>
          )}
        </Dropzone>
      )}

      {uploadProgress && (
        <div style={{marginTop: '12px'}}>
          <UploadProgressCard upload={uploadProgress} />
        </div>
      )}

      <ImageCropModal
        open={cropModalOpen}
        onClose={handleCropModalClose}
        onCropComplete={handleCropComplete}
        imageSrc={imageToCrop}
        aspectRatio={aspectRatio}
        fileName={fileNameToCrop}
      />
    </div>
  );
};

ImageUpload.propTypes = {
  imageUrl: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  uploading: PropTypes.bool,
  uploadProgress: PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
    progress: PropTypes.number.isRequired
  }),
  selectedFile: PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired
  }),
  placeholder: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    formats: PropTypes.string
  }),
  accept: PropTypes.string,
  disabled: PropTypes.bool,
  aspectRatio: PropTypes.number
};

export default ImageUpload;

